import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, Radio, Tooltip, message } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { A11Y_ROLES } from '@/utils/a11y-attrs';
import { roadTestApi } from '@/fetch/bussiness/roadtest/api';
import {
  ApiResponse,
  RoadTestItemDetail,
  RoadTestResult,
  RoadTestTaskDetail,
  RoadTestTaskType,
} from '@/fetch/bussiness/roadtest/types';
import { HttpStatusCode } from '@/fetch/core/constant';
import {
  LocalAnswers,
  buildLocalAnswers,
  localAnswersToItemResult,
  mergeRemoteDataToLocalAnswers,
  getIncompleteItemNames,
} from './utils';
import { goJiraPlatform } from '@/utils/util';
import TestItemList from './components/TestItemList';
import QuestionList from './components/QuestionList';

export interface EvaluationDrawerProps {
  vehicleName: string;
  taskNo: string;
  taskType: RoadTestTaskType;
  taskDetail: RoadTestTaskDetail | null;
  /** 保存成功后回调 */
  onSaveSuccess?: () => void;
  /** 提交成功后回调 */
  onSubmitSuccess?: () => void;
  /** 作废成功后回调 */
  onCancel?: () => void;
  /** 是否处于任务结束中态（仅作废后） */
  isTaskEnding?: boolean;
  /** 是否显示作废按钮默认 true整车回放场景传 false */
  showCancelButton?: boolean;
}

export const EVALUATION_DRAWER_TEST_ID = 'evaluation-drawer';

interface IssueNameTooltipProps {
  name: string;
}

const IssueNameTooltip: React.FC<IssueNameTooltipProps> = ({ name }) => (
  <Tooltip title={name} placement="topLeft" mouseEnterDelay={0.3}>
    <span className="item-name">{name}</span>
  </Tooltip>
);

const EMPTY_TASK_DETAIL: RoadTestTaskDetail = {
  taskNo: '',
  itemList: [],
  overallResult: null,
  currentRoadName: '',
  currentRoadNo: '',
};

const EvaluationDrawer: React.FC<EvaluationDrawerProps> = ({
  vehicleName,
  taskNo,
  taskType,
  taskDetail,
  onSaveSuccess,
  onSubmitSuccess,
  onCancel,
  isTaskEnding,
  showCancelButton = true,
}) => {
  const navigate = useNavigate();
  // 控制“首次拿到任务数据时自动选中当前测试项”的行为只执行一次
  const initializedActiveItemRef = React.useRef(false);

  // 本地化的题目答案状态，初始值为服务端返回的 taskDetail 转换而来
  const [localAnswers, setLocalAnswers] = useState<LocalAnswers>(() =>
    buildLocalAnswers(taskDetail || EMPTY_TASK_DETAIL)
  );

  // 当前选中的测试项
  const [activeItemNo, setActiveItemNo] = useState<string>();
  // 修改原因
  const [changeResultReason, setChangeResultReason] = useState('');
  // 整体测试结果
  const [overallResult, setOverallResult] = useState<RoadTestResult | null>(
    taskDetail?.overallResult ?? null
  );
  // 记录初始整体结果，用于判断是否需要填写修改原因
  const [initialOverallResult, setInitialOverallResult] = useState<
    RoadTestResult | null | undefined
  >(taskDetail?.overallResult);

  // 是否正在提交
  const [submitting, setSubmitting] = useState(false);
  // 是否正在保存
  const [saving, setSaving] = useState(false);
  // 是否正在作废
  const [cancelling, setCancelling] = useState(false);

  // 作废弹窗是否可见
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  // 作废理由
  const [cancelReason, setCancelReason] = useState('');

  // 提交确认弹窗是否可见
  const [submitConfirmVisible, setSubmitConfirmVisible] = useState(false);
  // 提交确认弹窗中显示的未填写完整的测试项名称列表
  const [submitConfirmNames, setSubmitConfirmNames] = useState<string[]>([]);

  // itemList 安全取值
  const itemList = taskDetail?.itemList || [];

  /**
   * taskNo 变化时彻底重置本地状态，避免上一任务结果残留
   */
  useEffect(() => {
    setLocalAnswers(buildLocalAnswers(EMPTY_TASK_DETAIL));
    setOverallResult(null);
    setInitialOverallResult(undefined);
    setChangeResultReason('');
    setActiveItemNo(undefined);
    initializedActiveItemRef.current = false;
    setCancelModalVisible(false);
    setCancelReason('');
    setSubmitting(false);
    setSaving(false);
    setCancelling(false);
    setSubmitConfirmVisible(false);
    setSubmitConfirmNames([]);
  }, [taskNo]);

  /**
   * ---详情数据变化时---
   * 合并规则：本地值 === null 时才覆盖，本地值 !== null 时不覆盖
   */
  useEffect(() => {
    if (!taskDetail) return;
    // 合并小题目：本地 result === null 时才覆盖
    setLocalAnswers((prev) => mergeRemoteDataToLocalAnswers(prev, taskDetail));

    // 合并整体结果：本地 overallResult === null 时才覆盖
    setOverallResult((prev) => {
      if (prev !== null) return prev; // 本地有值，不覆盖
      return taskDetail.overallResult ?? null; // 本地没值，用服务端的值
    });

    // 合并修改原因：本地 changeResultReason 为空（""）时才覆盖，用户已输入不覆盖
    setChangeResultReason((prev) => {
      if (prev) return prev;
      return taskDetail.modifyReason || '';
    });

    // 记录初始整体结果（仅首次赋值）
    setInitialOverallResult((prev) =>
      prev === undefined ? taskDetail.overallResult : prev
    );
  }, [taskDetail]);

  /**
   * ---首次拿到详情数据时---
   * 首次收到数据时设置选中的测试项
   */
  useEffect(() => {
    if (!initializedActiveItemRef.current && itemList.length) {
      // 根据 currentRoadNo 匹配测试项（实际上currentRoadNo就是itemNo）
      const matchedItem = taskDetail?.currentRoadNo
        ? itemList.find((item) => item.itemNo === taskDetail?.currentRoadNo)
        : undefined;
      setActiveItemNo(matchedItem?.itemNo ?? itemList[0].itemNo);
      initializedActiveItemRef.current = true;
    }
  }, [taskDetail, itemList]);

  /**
   * 当前选中的测试项对象
   */
  const activeItem = useMemo(() => {
    return itemList.find(
      (item: RoadTestItemDetail) => item.itemNo === activeItemNo
    );
  }, [itemList, activeItemNo]);

  /**
   * 是否正在加载任务详情数据
   */
  const loading = useMemo(() => {
    return !taskDetail;
  }, [taskDetail]);

  /**
   * 构建保存/提交参数
   */
  const buildParams = useCallback(
    () => ({
      vehicleName,
      taskNo,
      overallResult: overallResult ?? null,
      // 用户输入了修改原因就透传，由后端决定是否入库
      changeResultReason: changeResultReason || undefined,
      itemResult: localAnswersToItemResult(localAnswers),
    }),
    [
      vehicleName,
      taskNo,
      overallResult,
      changeResultReason,
      localAnswers,
    ]
  );

  /**
   * 点击保存后的回调
   */
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const params = buildParams();
      const res: ApiResponse = await roadTestApi.saveTaskDetail(params);
      if (res?.code === HttpStatusCode.Success) {
        message.success('保存成功');
        onSaveSuccess?.();
      } else {
        message.error(res?.message || '保存失败');
      }
    } catch (e) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  }, [buildParams, onSaveSuccess]);

  /**
   * 调用提交接口
   */
  const doSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const params = buildParams();
      const res: ApiResponse = await roadTestApi.submitTaskDetail(params);
      if (res?.code === HttpStatusCode.Success) {
        message.success('提交成功');
        onSubmitSuccess?.();
      } else {
        message.error(res?.message || '提交失败');
      }
    } catch (e) {
      message.error('提交失败');
    } finally {
      setSubmitting(false);
    }
  }, [buildParams, onSubmitSuccess]);

  /**
   * 点击提交按钮后的回调，包含校验逻辑
   */
  const handleSubmit = useCallback(async () => {
    // 提交校验顺序：
    // 1. 是否填写整体结果
    // 2. 是否需要填写修改原因
    // 3. 整体结果有值时，检查各测试项是否填写完整

    // 整体结果未选择时，提示用户选择整体结果
    if (!overallResult) {
      message.warning('请选择整体结果');
      return;
    }
    // 首次提交（之前结果为null）且选择通过，不需要填原因
    // 其他情况都需要填原因：首次选择不通过、或已有结果后修改
    const isFirstSubmit =
      initialOverallResult === null || initialOverallResult === undefined;
    const isFirstPass = isFirstSubmit && overallResult === RoadTestResult.PASS;
    if (!isFirstPass && !changeResultReason) {
      message.warning('请填写修改原因');
      return;
    }

    // 整体结果有值时，检查是否存在未填写完整的测试项
    const incompleteNames = getIncompleteItemNames(itemList, localAnswers);
    if (incompleteNames.length > 0) {
      setSubmitConfirmNames(incompleteNames);
      setSubmitConfirmVisible(true);
      return;
    }

    await doSubmit();
  }, [
    overallResult,
    initialOverallResult,
    changeResultReason,
    itemList,
    localAnswers,
    doSubmit,
  ]);

  /**
   * 点击提交确认弹窗的确认按钮后的回调
   */
  const handleConfirmSubmit = useCallback(async () => {
    setSubmitConfirmVisible(false);
    await doSubmit();
  }, [doSubmit]);

  /**
   * 点击作废按钮后的回调
   */
  const handleCancel = useCallback(async () => {
    if (!cancelReason) {
      message.warning('请填写作废原因');
      return;
    }
    setCancelling(true);
    try {
      const res: ApiResponse = await roadTestApi.cancelTask({
        vehicleName,
        taskNo,
        cancelReason,
      });
      if (res?.code === HttpStatusCode.Success) {
        message.success('作废成功');
        setCancelModalVisible(false);
        onCancel?.();
      } else {
        message.error(res?.message || '作废失败');
      }
    } catch (e) {
      message.error('作废失败');
    } finally {
      setCancelling(false);
    }
  }, [cancelReason, vehicleName, taskNo, onCancel]);

  /**
   * 整体结果选择变化后的回调
   */
  const handleOverallResultChange = useCallback(
    (e: RadioChangeEvent) => setOverallResult(e.target.value as RoadTestResult),
    []
  );

  /**
   * 修改原因输入框变化后的回调
   */
  const handleChangeReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      setChangeResultReason(e.target.value),
    []
  );

  /**
   * 作废原因输入框变化后的回调
   */
  const handleCancelReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      setCancelReason(e.target.value),
    []
  );

  /**
   * 点击作废按钮后的回调
   */
  const handleCancelBtnClick = useCallback(() => {
    setCancelModalVisible(true);
  }, []);

  /**
   * 点击作废弹窗的取消按钮后的回调
   */
  const handleModalCancel = useCallback(() => {
    setCancelModalVisible(false);
    setCancelReason('');
  }, []);

  /**
   * 点击提交确认弹窗的取消按钮后的回调
   */
  const handleSubmitConfirmCancel = useCallback(() => {
    setSubmitConfirmVisible(false);
  }, []);

  const hasDefects = (taskDetail?.defectList?.length || 0) > 0;
  const hasAccidents = (taskDetail?.accidentList?.length || 0) > 0;

  return (
    <div className="evaluation-drawer" data-testid={EVALUATION_DRAWER_TEST_ID}>
      {/* 进行态占位 */}
      {isTaskEnding && (
        <div
          className="evaluation-ending-overlay"
          data-testid="evaluation-ending-overlay"
        >
          <div className="evaluation-ending-content">任务结束中，请稍候...</div>
        </div>
      )}
      {loading ? (
        <div className="evaluation-loading">等待任务数据...</div>
      ) : taskDetail ? (
        <>
          {/* 测试项 + 题目 */}
          <div className="evaluation-section section-questions">
            <div className="scheme-section-title">测试项与题目</div>
            <div className="evaluation-layout">
              <div className="evaluation-left">
                <TestItemList
                  itemList={itemList}
                  localAnswers={localAnswers}
                  activeItemNo={activeItemNo}
                  onSelect={setActiveItemNo}
                />
              </div>
              <div className="evaluation-right">
                {activeItem && (
                  <QuestionList
                    item={activeItem}
                    localAnswers={localAnswers}
                    onChange={setLocalAnswers}
                  />
                )}
              </div>
            </div>
          </div>

          {/* 缺陷和事故列表 */}
          {(hasDefects || hasAccidents) && (
            <div
              className="evaluation-section section-issues"
              data-testid="defect-accident-list"
            >
              {/* 缺陷列表 */}
              {hasDefects && (
                <div className="defect-section">
                  <div className="scheme-section-title">缺陷列表</div>
                  <div className="defect-list-items">
                    {(taskDetail?.defectList || []).map((defect) => (
                      <div key={defect.defectNo} className="list-item">
                        <IssueNameTooltip name={defect.name} />
                        <a
                          className="item-no"
                          onClick={() => goJiraPlatform(defect.defectNo)}
                          data-testid={`defect-link-${defect.defectNo}`}
                        >
                          {defect.defectNo}
                        </a>
                        <span className="item-time">{defect.defectTime}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 事故列表 */}
              {hasAccidents && (
                <div className="accident-section">
                  <div className="scheme-section-title">事故列表</div>
                  <div className="accident-list-items">
                    {(taskDetail?.accidentList || []).map((accident) => (
                      <div key={accident.accidentNo} className="list-item">
                        <IssueNameTooltip name={accident.name} />
                        <a
                          className="item-no"
                          onClick={() =>
                            navigate(
                              `/app/accidentmanage?activeTab=menuAccidentManagement&accidentNo=${encodeURIComponent(accident.accidentNo)}`
                            )
                          }
                          data-testid={`accident-link-${accident.accidentNo}`}
                        >
                          {accident.accidentNo}
                        </a>
                        <span className="item-time">
                          {accident.accidentTime}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 整体结果 */}
          <div className="evaluation-section section-overall">
            <div className="scheme-section-title">整体结果</div>
            <div className="overall-result-section">
              <Radio.Group
                value={overallResult}
                onChange={handleOverallResultChange}
                data-testid="evaluation-overall-result"
              >
                <Radio value={RoadTestResult.PASS}>通过</Radio>
                <Radio value={RoadTestResult.FAIL}>不通过</Radio>
              </Radio.Group>
              {/* 修改原因输入框 */}
              <Input.TextArea
                autoSize={{ minRows: 3, maxRows: 5 }}
                value={changeResultReason}
                onChange={handleChangeReasonChange}
                placeholder="请填写修改原因"
                data-testid="evaluation-change-reason"
                maxLength={100}
                showCount
              />
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="drawer-footer">
            <Button
              loading={saving}
              onClick={handleSave}
              disabled={isTaskEnding}
              data-testid="evaluation-save-btn"
            >
              保存
            </Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={handleSubmit}
              disabled={isTaskEnding}
              data-testid="evaluation-submit-btn"
            >
              提交
            </Button>
            {showCancelButton && (
              <Button
                danger
                onClick={handleCancelBtnClick}
                disabled={isTaskEnding}
                data-testid="evaluation-cancel-btn"
              >
                作废
              </Button>
            )}
          </div>
        </>
      ) : null}
      {/* 作废弹窗 */}
      <Modal
        title="作废任务"
        open={cancelModalVisible}
        onOk={handleCancel}
        onCancel={handleModalCancel}
        confirmLoading={cancelling}
        okText="确认"
        cancelText="取消"
        data-testid="evaluation-cancel-modal"
        okButtonProps={{ 'data-testid': 'evaluation-cancel-confirm-btn' }}
        {...A11Y_ROLES.dialog('作废任务确认')}
        aria-labelledby="evaluation-cancel-modal-title"
      >
        <Input.TextArea
          rows={3}
          value={cancelReason}
          onChange={handleCancelReasonChange}
          placeholder="请填写作废原因"
          data-testid="evaluation-cancel-reason"
          maxLength={50}
          showCount
        />
      </Modal>
      {/* 提交确认弹窗 */}
      <Modal
        title="提交确认"
        open={submitConfirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={handleSubmitConfirmCancel}
        confirmLoading={submitting}
        okText="确认"
        cancelText="取消"
        data-testid="evaluation-submit-confirm-modal"
        okButtonProps={{ 'data-testid': 'evaluation-submit-confirm-ok' }}
        cancelButtonProps={{
          'data-testid': 'evaluation-submit-confirm-cancel',
        }}
      >
        <div data-testid="evaluation-submit-confirm-content">
          {`「${submitConfirmNames.join('；')}」测试结果未填写全，确认提交？`}
        </div>
      </Modal>
    </div>
  );
};

export default React.memo(EvaluationDrawer);
